<!-- 自营视角：店铺形象（不复用「店铺管理」多店菜单） -->
<template>
  <ContentWrap title="店铺形象">
    <el-alert
      type="info"
      :closable="false"
      show-icon
      class="mb-16px"
      title="自营模式下在此维护本店 Logo / 头图 / 简介，供 C 端店铺页展示。"
      description="不提供入驻审核、多店列表与店铺类型变更。"
    />

    <div v-loading="loading">
      <el-empty v-if="!loading && shops.length === 0" description="暂无自营店，请刷新或确认有建店权限" />

      <el-card v-for="shop in shops" :key="shop.id" class="mb-16px" shadow="never">
        <div class="flex items-start gap-16px flex-wrap">
          <el-image
            v-if="shop.shopLogo"
            :src="shop.shopLogo"
            class="h-64px w-64px rounded-8px shrink-0"
            fit="cover"
          />
          <div
            v-else
            class="h-64px w-64px rounded-8px shrink-0 bg-[var(--el-fill-color)] flex items-center justify-center text-20px text-[var(--el-text-color-secondary)]"
          >
            {{ (shop.shopName || '店').slice(0, 1) }}
          </div>
          <div class="flex-1 min-w-200px">
            <div class="text-16px font-600 mb-4px">{{ shop.shopName || `店铺${shop.id}` }}</div>
            <div class="text-12px text-[var(--el-text-color-secondary)] mb-8px">
              店铺 ID {{ shop.id }} · 平台自营
            </div>
            <div class="text-13px text-[var(--el-text-color-regular)] line-clamp-2">
              {{ shop.shopDesc || '暂无简介' }}
            </div>
          </div>
          <el-button
            type="primary"
            v-hasPermi="['merchant:shop:update']"
            @click="openEdit(shop)"
          >
            编辑形象
          </el-button>
        </div>
      </el-card>
    </div>
  </ContentWrap>

  <el-dialog v-model="visible" title="编辑店铺形象" width="560px" destroy-on-close>
    <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
      <el-form-item label="店铺名称" prop="shopName">
        <el-input v-model="form.shopName" placeholder="请输入店铺名称" maxlength="100" />
      </el-form-item>
      <el-form-item label="店铺Logo">
        <UploadImg v-model="form.shopLogo" width="100px" height="100px" directory="shop/logo" />
        <div class="text-12px text-[var(--el-text-color-secondary)] mt-4px">
          C 端店铺头像；清空后显示店名首字
        </div>
      </el-form-item>
      <el-form-item label="店铺头图">
        <UploadImg
          v-model="form.shopBanner"
          width="240px"
          height="100px"
          directory="shop/banner"
        />
        <div class="text-12px text-[var(--el-text-color-secondary)] mt-4px">
          C 端顶部背景；建议宽图约 750×300
        </div>
      </el-form-item>
      <el-form-item label="店铺简介">
        <el-input
          v-model="form.shopDesc"
          type="textarea"
          :rows="3"
          placeholder="请输入店铺简介"
          maxlength="1000"
          show-word-limit
        />
      </el-form-item>
      <el-form-item label="联系电话">
        <el-input v-model="form.contactPhone" placeholder="请输入联系电话" maxlength="20" />
      </el-form-item>
      <el-divider content-position="left">退货地址（买家退货必填）</el-divider>
      <el-form-item label="收件人" prop="returnName">
        <el-input v-model="form.returnName" placeholder="退货收件人" maxlength="64" />
      </el-form-item>
      <el-form-item label="收件手机" prop="returnMobile">
        <el-input v-model="form.returnMobile" placeholder="退货收件手机" maxlength="20" />
      </el-form-item>
      <el-form-item label="所在地区" prop="returnAreaId">
        <el-cascader
          v-model="form.returnAreaId"
          :options="areaList"
          :props="areaProps"
          clearable
          placeholder="请选择省市区"
          class="!w-full"
        />
      </el-form-item>
      <el-form-item label="详细地址" prop="returnDetailAddress">
        <el-input v-model="form.returnDetailAddress" placeholder="街道门牌等详细地址" maxlength="255" />
      </el-form-item>
      <el-divider content-position="left">团购司机取货坐标（选填）</el-divider>
      <el-form-item label="取货经度">
        <el-input-number v-model="form.longitude" :precision="6" class="!w-full" placeholder="如 118.79" />
      </el-form-item>
      <el-form-item label="取货纬度">
        <el-input-number v-model="form.latitude" :precision="6" class="!w-full" placeholder="如 32.06" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="visible = false">取消</el-button>
      <el-button type="primary" :loading="submitting" @click="save">保存</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { ContentWrap } from '@/components/ContentWrap'
import { UploadImg } from '@/components/UploadFile'
import { defaultProps } from '@/utils/tree'
import { getAreaTree } from '@/api/system/area'
import { getShopPage, updateShopProfile } from '@/api/merchant/shop-manager'
import {
  ensureDefaultSelfOperatedShops,
  MALL_SELF_OPERATED_SHOP_TYPE
} from '@/utils/mallSelfOperatedShop'

defineOptions({ name: 'MallShopProfile' })

type ShopRow = {
  id: number
  shopName?: string
  shopLogo?: string
  shopBanner?: string
  shopDesc?: string
  contactPhone?: string
  shopType?: number
  returnName?: string
  returnMobile?: string
  returnAreaId?: number
  returnDetailAddress?: string
  longitude?: number
  latitude?: number
}

const loading = ref(false)
const shops = ref<ShopRow[]>([])
const visible = ref(false)
const submitting = ref(false)
const formRef = ref()
const areaList = ref<any[]>([])
const areaProps = { ...defaultProps, emitPath: false }
const form = reactive({
  id: 0,
  shopName: '',
  shopLogo: '',
  shopBanner: '',
  shopDesc: '',
  contactPhone: '',
  returnName: '',
  returnMobile: '',
  returnAreaId: undefined as number | undefined,
  returnDetailAddress: '',
  longitude: undefined as number | undefined,
  latitude: undefined as number | undefined
})
const rules = {
  shopName: [{ required: true, message: '请输入店铺名称', trigger: 'blur' }],
  returnName: [{ required: true, message: '请输入退货收件人', trigger: 'blur' }],
  returnMobile: [
    { required: true, message: '请输入退货收件手机', trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
  ],
  returnAreaId: [{ required: true, message: '请选择退货地区', trigger: 'change' }],
  returnDetailAddress: [{ required: true, message: '请输入退货详细地址', trigger: 'blur' }]
}

const loadShops = async () => {
  loading.value = true
  try {
    await ensureDefaultSelfOperatedShops()
    const data = await getShopPage({
      pageNo: 1,
      pageSize: 50,
      shopType: MALL_SELF_OPERATED_SHOP_TYPE
    })
    shops.value = (data?.list || []).map((row: any) => ({
      id: Number(row.id),
      shopName: row.shopName,
      shopLogo: row.shopLogo,
      shopBanner: row.shopBanner,
      shopDesc: row.shopDesc,
      contactPhone: row.contactPhone,
      shopType: row.shopType,
      returnName: row.returnName,
      returnMobile: row.returnMobile,
      returnAreaId: row.returnAreaId,
      returnDetailAddress: row.returnDetailAddress,
      longitude: row.longitude,
      latitude: row.latitude
    }))
  } finally {
    loading.value = false
  }
}

const openEdit = (shop: ShopRow) => {
  form.id = shop.id
  form.shopName = shop.shopName || ''
  form.shopLogo = shop.shopLogo || ''
  form.shopBanner = shop.shopBanner || ''
  form.shopDesc = shop.shopDesc || ''
  form.contactPhone = shop.contactPhone || ''
  form.returnName = shop.returnName || ''
  form.returnMobile = shop.returnMobile || ''
  form.returnAreaId = shop.returnAreaId
  form.returnDetailAddress = shop.returnDetailAddress || ''
  form.longitude = shop.longitude ?? undefined
  form.latitude = shop.latitude ?? undefined
  visible.value = true
}

const save = async () => {
  const valid = await formRef.value?.validate().catch(() => false)
  if (!valid) return
  submitting.value = true
  try {
    await updateShopProfile({
      id: form.id,
      shopName: form.shopName,
      shopLogo: form.shopLogo || undefined,
      shopBanner: form.shopBanner || undefined,
      shopDesc: form.shopDesc || undefined,
      contactPhone: form.contactPhone || undefined,
      returnName: form.returnName.trim(),
      returnMobile: form.returnMobile.trim(),
      returnAreaId: form.returnAreaId!,
      returnDetailAddress: form.returnDetailAddress.trim(),
      longitude: form.longitude,
      latitude: form.latitude
    })
    ElMessage.success('店铺形象已保存')
    visible.value = false
    await loadShops()
  } finally {
    submitting.value = false
  }
}

onMounted(async () => {
  areaList.value = (await getAreaTree()) || []
  void loadShops()
})
</script>
